import { useMemo } from 'react'
import { DatePicker, Form, Select, Space, Tooltip } from 'antd'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import {
  SUBSCRIBER_STATES,
  DELIVERY_METHODS,
  countClosedSubscribers,
  fetchSubscribers,
} from './subscriberApi'
import { AgentSelect, RESELLER_KINDS } from '@/features/agent/AgentSelect'
import { queryKeys } from '@/shared/api/queryKeys'
import { usePlanOptions, useVispOptions } from '@/shared/api/reference'
import { usePermissions } from '@/shared/auth/usePermissions'
import { useAuth } from '@/shared/auth/useAuth'
import { useActiveBranches } from '@/features/branch/useActiveBranches'
import { branchLabel } from '@/features/branch/branchApi'
import { rangeToIso } from '@/shared/lib/dayRange'
import { getCurrencyCode } from '@/shared/lib/format'
import { money } from '@/shared/lib/money'
import { MoneyInput } from '@/shared/ui/MoneyInput'
import { displayName } from '@/shared/lib/planName'
import { FilterDrawer } from '@/shared/ui/FilterDrawer'
import { ClosedHiddenTag } from '@/shared/ui/table/ClosedHidden'
import { useClosedHidden } from '@/shared/ui/table/useClosedHidden'
import type { TableState } from '@/shared/ui/table/tableState'
import { useTableUrlState } from '@/shared/ui/table/useTableUrlState'
import type { SubscriberRow } from './subscriberApi'
import { CUSTOMER_TYPES, DEVICE_OWNERSHIPS } from './profileApi'
import { DeviceTypeSelect } from './DeviceTypeSelect'
import { VispSelect } from './VispSelect'

type Filters = TableState['filters']

const BALANCE_FLOOR = -999_999_999_999
const NO_BRANCH = 'none'

/**
 * Abonentlar ro'yxati uchun KO'RINADIGAN filtrlar.
 *
 * <h2>⚠️ Filtrlar allaqachon bor edi — lekin ularni hech kim topmasdi</h2>
 * Holat va tur bo'yicha filtr jadval sarlavhasidagi kichik voronka
 * ikonkasi ichida turardi (antd `column.filters`). Texnik jihatdan
 * ishlardi, amalda esa yo'q edi: egasi "filtrlar yo'q" deb xabar
 * qildi, chunki ko'rinmagan narsa mavjud emas bilan barobar.
 *
 * <h2>⚠️ Nega bu holatni O'ZI saqlamaydi</h2>
 * Jadval holati URL'da yashaydi ({@code useTableUrlState}). Bu panel
 * AYNAN o'sha hookdan foydalanadi, ya'ni:
 * <ul>
 *   <li>voronka ichidagi eski filtr va bu panel BIR XIL holatni
 *       ko'rsatadi — ikkitasi bir-biriga zid bo'lib qolmaydi;</li>
 *   <li>filtrlangan ro'yxatning havolasini yuborish mumkin;</li>
 *   <li>brauzerning "orqaga" tugmasi ishlaydi.</li>
 * </ul>
 *
 * <p>O'z {@code useState} ini qo'yish ikkinchi haqiqat manbai bo'lardi
 * va ular birinchi qayta chizishdayoq ajralib ketardi.
 *
 * <h2>⚠️ Nega HOLAT tashqarida, qolgani panelda (egasi qarori 2026-08-20)</h2>
 * To'rtta tanlagich bir qatorga sig'masdi va ikkinchi qatorga tushardi;
 * beshinchisi qo'shilsa uchinchi qator paydo bo'lardi. Lekin HAMMASINI
 * panelga yashirish ham yechim emas: kassir kun bo'yi «faol / bloklangan»
 * bo'yicha ajratadi va uni har safar panel ochishga majburlash
 * ikki bosishni har amalga qo'shardi. Shuning uchun eng ko'p
 * ishlatiladigani — holat — tashqarida qoladi.
 *
 * <p>Matnli qidiruv ham tashqarida: uni {@code DataTable} o'zi chizadi.
 */
export function SubscriberFilterBar() {
  const { t } = useTranslation()
  const { state, patch } = useTableUrlState()

  const stateFilter = asSingle(state.filters['state'])
  const kindFilter = asSingle(state.filters['kind'])
  const phoneFilter = asSingle(state.filters['phone'])
  const agentFilter = asSingle(state.filters['agent'])
  /**
   * ⚠️ "Yopilganlarni ko'rsatish" bayrog'i OLIB TASHLANGAN (egasi qarori):
   * holat filtridagi `CLOSED` bilan ikkilanish paydo qilardi.
   */
  /*
    ⚠️ `agentFilter` sanoqqa `agent.read` huquqidan QAT'I NAZAR kiradi.
    Huquqsiz foydalanuvchida tanlov o'zi chizilmaydi, lekin havola
    orqali kelgan filtr KUCHDA qoladi: uni sanamasak ro'yxat torayib
    turgan holda tugmadagi nishon bo'sh bo'lardi va operator torayish
    sababini ko'rmasdi.

    ⚠️ Nishon FAQAT paneldagi filtrlarni sanaydi. Holat tanlagichi
    ekranda o'zi ko'rinib turadi — uni ham sanash yopiq panelda
    yashiringan filtr bor degan yolg'on signal berardi.
  */
  const active = [
    asSingle(state.filters['customerType']),
    asSingle(state.filters['deviceType']),
    asSingle(state.filters['deviceOwnership']),
    asSingle(state.filters['visp']),
    asSingle(state.filters['center']),
    asSingle(state.filters['plan']),
    asSingle(state.filters['expiresFrom']),
    asSingle(state.filters['createdFrom']),
    asSingle(state.filters['balanceMin']),
    asSingle(state.filters['balanceMax']),
    asSingle(state.filters['branch']),
    asSingle(state.filters['noBranch']),
    kindFilter,
    phoneFilter,
    agentFilter].filter(Boolean).length

  /**
   * ⚠️ YASHIRINGAN YOPILGANLAR SONI — ADR-033 §5, BESH SAHIFADA
   * ALLAQACHON BOR EDI, MANBA SAHIFASIDA ESA YO'Q EDI.
   *
   * Ro'yxat yopilgan abonentlarni sukut bo'yicha yashiradi
   * (`subscriberListParams` javadoc'i) va bu to'g'ri qaror. Lekin buni
   * ekranda AYTADIGAN hech narsa yo'q edi — bugungi bazada 72 ta qator
   * jimgina tushib qolardi va operator yopilgan abonentni «o'chib
   * ketgan» deb o'qirdi. Aynan shu qoidaning eng qimmat yon ta'siri
   * (`ClosedHidden` izohi) va u boshqa joyda to'xtatilgan, bu yerda esa
   * yo'q edi.
   *
   * ⚠️ Kalit `DataTable` nikidan AYNAN nusxa (`queryKeys.subscribers.all`
   * + `fetchSubscribers`): React Query ikkala kuzatuvchiga BITTA
   * javobni beradi, ya'ni ko'rinayotgan qatorlar soni uchun ikkinchi
   * so'rov ketmaydi. Kalit ajralib ketsa nuqson jim bo'lardi.
   *
   * ⚠️ FAQAT SUKUT KO'RINISHDA so'raladi: holat tanlangan bo'lsa
   * torayish operatorning O'Z tanlovi va yashiringan qator degan tushuncha
   * yo'q — so'rov ham keraksiz.
   */
  const hiddenCounts = useClosedHidden<SubscriberRow>({
    queryKey: queryKeys.subscribers.all,
    state,
    fetchPage: fetchSubscribers,
    countClosed: countClosedSubscribers,
    enabled: stateFilter === undefined,
  })

  /**
   * ⚠️ Filtr o'zgarganda sahifa BIRINCHIGA qaytariladi.
   *
   * Usiz operator 5-sahifada turib filtr qo'ysa, natijada 2 sahifa
   * qolsa — bo'sh ekran ko'rardi va "hech narsa topilmadi" deb
   * o'ylardi.
   */
  const commit = (next: Filters) => {
    patch({ page: 1, filters: next })
  }

  return (
    <Space wrap size="small">
      {/* ⚠️ Tanlagichda izoh: yopilganlarni ko'rishning YAGONA yo'li shu
          va u ekrandan ko'rinmaydi (`InventoryPage` bilan bir xil naqsh) */}
      <Tooltip title={t('subscribers.filters.closedHiddenHint')}>
        <Select
          allowClear
          style={{ minWidth: 150 }}
          placeholder={t('subscribers.columns.status')}
          value={stateFilter}
          onChange={(v?: string) => commit({ ...state.filters, state: v ? [v] : [] })}
          options={SUBSCRIBER_STATES.map((s) => ({
            value: s,
            label: t(`subscriberState.${s}`),
          }))}
        />
      </Tooltip>
      {/* ⚠️ Tugma «Barcha holatlar» EMAS, «Yopilganlarni ko'rsatish»:
          bu ro'yxatda `includeClosed` FAQAT `state=CLOSED` bilan
          yoqiladi (`subscriberListParams`), ya'ni ochiq va yopilganni
          BIRGA ko'rsatadigan holat umuman yo'q. «Barcha holatlar» deb
          yozish tugma qilmaydigan ishni va'da qilardi. */}
      <Select
        allowClear
        style={{ minWidth: 130 }}
        placeholder={t('status.onlineTitle')}
        value={asSingle(state.filters['online'])}
        onChange={(v?: string) => commit({ ...state.filters, online: v ? [v] : [] })}
        options={[
          { value: 'online', label: t('subscribers.filters.online') },
          { value: 'offline', label: t('subscribers.filters.offline') },
        ]}
      />
      <ClosedHiddenTag
        counts={hiddenCounts}
        showAllLabel={t('subscribers.filters.closedOnly')}
        onShowAll={() => commit({ ...state.filters, state: ['CLOSED'] })}
      />
      <FilterDrawer<Filters>
        value={state.filters}
        /* «Tozalash» faqat SHU PANELdagi maydonlarni bo'shatadi —
           tashqaridagi holat tanlovi operatorning ko'z oldida turibdi
           va uni panel ichidan jimgina o'chirish kutilmagan bo'lardi. */
        resetValue={{
          ...state.filters,
          customerType: [],
          deviceType: [],
          deviceOwnership: [],
          kind: [],
          phone: [],
          agent: [],
          visp: [],
          center: [],
          plan: [],
          expiresFrom: [],
          expiresTo: [],
          createdFrom: [],
          createdTo: [],
          balanceMin: [],
          balanceMax: [],
          branch: [],
          noBranch: [],
        }}
        activeCount={active}
        onApply={commit}
      >
        {(draft, patchDraft) => (
          <SubscriberFilterFields
            draft={draft}
            setFilter={(key, value) => patchDraft({ [key]: value ? [value] : [] })}
            setMany={(key, values) => patchDraft({ [key]: values })}
            setRange={(fromKey, toKey, from, to) =>
              patchDraft({ [fromKey]: from ? [from] : [], [toKey]: to ? [to] : [] })
            }
          />
        )}
      </FilterDrawer>
    </Space>
  )
}

/**
 * Panel ichidagi maydonlar — hammasi QORALAMA ustida.
 *
 * ⚠️ Bu yerda hech narsa URL'ga yozilmaydi: `setFilter` qoralamani
 * o'zgartiradi, so'rov esa faqat «Qo'llash» dan keyin ketadi.
 */
function SubscriberFilterFields({
  draft,
  setFilter,
  setMany,
  setRange,
}: {
  draft: Filters
  setFilter: (key: string, value: string | undefined) => void
  setMany: (key: string, values: string[]) => void
  setRange: (fromKey: string, toKey: string, from: string | null, to: string | null) => void
}) {
  const { t, i18n } = useTranslation()
  const { can } = usePermissions()
  const visps = useVispOptions(can('network.visp.read'))
  const plans = usePlanOptions(can('plan.read'))
  const centers = useMemo(
    () => [...new Map(visps.map((v) => [v.centerId, v.centerName])).entries()].map(([value, label]) => ({ value, label })),
    [visps],
  )
  const expiresFrom = asSingle(draft['expiresFrom'])
  const expiresTo = asSingle(draft['expiresTo'])
  const { options: branchChoices } = useActiveBranches()
  const isTenantWide = useAuth().user?.scopes.some((scope) => scope.type === 'TENANT' || scope.type === 'GLOBAL') ?? false
  const createdFrom = asSingle(draft['createdFrom'])
  const createdTo = asSingle(draft['createdTo'])
  const currency = useAuth().user?.tenant.currency ?? getCurrencyCode()
  const balanceBound = (key: string) => {
    const raw = asSingle(draft[key])
    return raw === undefined ? null : money(Number(raw), currency)
  }

  return (
    <>
      <Form.Item label={t('subscriber.profile.customerType')}>
        <Select
          allowClear
          style={{ width: '100%' }}
          placeholder={t('subscriber.profile.customerType')}
          value={asSingle(draft['customerType'])}
          onChange={(v?: string) => setFilter('customerType', v)}
          options={CUSTOMER_TYPES.map((v) => ({ value: v, label: t(`subscriber.customerType.${v}`) }))}
        />
      </Form.Item>
      <Form.Item label={t('subscriber.profile.deviceType')}>
        <DeviceTypeSelect
          includeInactive
          allowClear
          placeholder={t('subscriber.profile.deviceType')}
          value={asSingle(draft['deviceType'])}
          onChange={(v) => setFilter('deviceType', v)}
        />
      </Form.Item>
      <Form.Item label={t('subscriber.profile.deviceOwnership')}>
        <Select
          allowClear
          style={{ width: '100%' }}
          placeholder={t('subscriber.profile.deviceOwnership')}
          value={asSingle(draft['deviceOwnership'])}
          onChange={(v?: string) => setFilter('deviceOwnership', v)}
          options={DEVICE_OWNERSHIPS.map((v) => ({ value: v, label: t(`subscriber.deviceOwnership.${v}`) }))}
        />
      </Form.Item>
      <Form.Item label={t('subscribers.columns.kind')}>
        <Select
          allowClear
          style={{ width: '100%' }}
          placeholder={t('subscribers.columns.kind')}
          value={asSingle(draft['kind'])}
          onChange={(v?: string) => setFilter('kind', v)}
          options={DELIVERY_METHODS.map((k) => ({
            value: k,
            label: t(`deliveryMethod.${k}`),
          }))}
        />
      </Form.Item>
      {/*
        ⚠️ «Telefoni yo'q» — bu qulaylik filtri EMAS, bo'shliqni
        ko'rsatadigan vosita.

        Telefon maydoni ixtiyoriy (mavjud abonentlarda raqam yo'q va
        ularni tahrirlash imkoni yo'qolmasligi kerak). Lekin raqamsiz
        abonentga SMS bormaydi — va busiz buni hech kim sezmasdi: xato
        yo'q, log toza, ekranda belgi yo'q. Filtr o'sha bo'shliqni
        sanaladigan va tuzatiladigan qiladi.
      */}
      <Form.Item label={t('subscriber.phone')}>
        <Select
          allowClear
          style={{ width: '100%' }}
          placeholder={t('subscriber.phone')}
          value={asSingle(draft['phone'])}
          onChange={(v?: string) => setFilter('phone', v)}
          options={[
            { value: 'missing', label: t('subscribers.filters.phoneMissing') },
            { value: 'present', label: t('subscribers.filters.phonePresent') },
          ]}
        />
      </Form.Item>
      {/*
        Agent bo'yicha filtr.

        ⚠️ RO'YXAT DOIRA BO'YICHA FILTRLANGAN va bu mantiq bu yerda
        TAKRORLANMAYDI: `AgentSelect` `useAgentIndex` dan o'qiydi, u esa
        serverning doira predikatidan o'tgan javobni beradi. Nusxa
        yasash QARORLAR.md §19.8 da o'lchangan xatoning aynan o'zi
        bo'lardi — nusxa ajrashganda natija xato emas, JIMGINA BO'SH
        bo'ladi.

        ⚠️ `agent.read` YO'Q bo'lsa tanlov umuman chizilmaydi. Bo'sh
        ro'yxatli tanlovni qoldirish «doirangizda agent yo'q» degan
        YOLG'ON xulosaga olib borardi, holbuki sabab boshqa — huquq.
      */}
      {can('plan.read') ? (
        <Form.Item label={t('subscribers.columns.tariff')}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
            placeholder={t('subscribers.columns.tariff')}
            value={asSingle(draft['plan'])}
            onChange={(v?: string) => setFilter('plan', v)}
            options={plans.map((plan) => ({ value: plan.id, label: displayName(plan, i18n.language) }))}
          />
        </Form.Item>
      ) : null}
      <Form.Item label={t('subscribers.filters.expiresBetween')}>
        <DatePicker.RangePicker
          allowClear
          style={{ width: '100%' }}
          value={expiresFrom && expiresTo ? [dayjs(expiresFrom), dayjs(expiresTo)] : null}
          onChange={(value) => {
            const picked = value && value[0] && value[1] ? rangeToIso([value[0], value[1]]) : null
            setRange('expiresFrom', 'expiresTo', picked?.from ?? null, picked?.to ?? null)
          }}
        />
      </Form.Item>
      {branchChoices.length > 0 ? (
        <Form.Item label={t('branch.label')}>
          <Select<string[]>
            mode="multiple"
            allowClear
            optionFilterProp="label"
            style={{ width: '100%' }}
            placeholder={t('branch.all')}
            value={[...(draft['branch'] ?? []), ...(asSingle(draft['noBranch']) === 'yes' ? [NO_BRANCH] : [])]}
            onChange={(values) => {
              setMany('branch', values.filter((v) => v !== NO_BRANCH))
              setFilter('noBranch', values.includes(NO_BRANCH) ? 'yes' : undefined)
            }}
            options={[
              ...(can('subscriber.update') && isTenantWide ? [{ value: NO_BRANCH, label: t('branch.none') }] : []),
              ...branchChoices.map((b) => ({ value: b.id, label: branchLabel(b) })),
            ]}
          />
        </Form.Item>
      ) : null}
      <Form.Item label={t('subscribers.filters.createdBetween')}>
        <DatePicker.RangePicker
          allowClear
          style={{ width: '100%' }}
          value={createdFrom && createdTo ? [dayjs(createdFrom), dayjs(createdTo)] : null}
          onChange={(value) => {
            const picked = value && value[0] && value[1] ? rangeToIso([value[0], value[1]]) : null
            setRange('createdFrom', 'createdTo', picked?.from ?? null, picked?.to ?? null)
          }}
        />
      </Form.Item>
      <Form.Item label={t('subscribers.filters.balanceBetween')} extra={t('subscribers.filters.balanceHint')}>
        <Space.Compact block>
          <MoneyInput
            currency={currency}
            minMinor={BALANCE_FLOOR}
            placeholder={t('subscribers.filters.from')}
            value={balanceBound('balanceMin')}
            onChange={(v) => setFilter('balanceMin', v ? String(v.amount) : undefined)}
            style={{ width: '50%' }}
          />
          <MoneyInput
            currency={currency}
            minMinor={BALANCE_FLOOR}
            placeholder={t('subscribers.filters.to')}
            value={balanceBound('balanceMax')}
            onChange={(v) => setFilter('balanceMax', v ? String(v.amount) : undefined)}
            style={{ width: '50%' }}
          />
        </Space.Compact>
      </Form.Item>
      {can('network.visp.read') ? (
        <Form.Item label={t('subscribers.filters.center')}>
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            style={{ width: '100%' }}
            placeholder={t('subscribers.filters.center')}
            value={asSingle(draft['center'])}
            onChange={(v?: string) => setFilter('center', v)}
            options={centers}
          />
        </Form.Item>
      ) : null}
      {can('network.visp.read') ? (
        <Form.Item label={t('subscriber.visp.label')}>
          <VispSelect
            allowClear
            placeholder={t('subscriber.visp.label')}
            value={asSingle(draft['visp'])}
            onChange={(v) => setFilter('visp', v)}
          />
        </Form.Item>
      ) : null}
      {can('agent.read') ? (
        <Form.Item label={t('subscribers.filters.agent')}>
          <AgentSelect
            kinds={RESELLER_KINDS}
            value={asSingle(draft['agent']) ?? null}
            onChange={(v) => setFilter('agent', v ?? undefined)}
            placeholder={t('subscribers.filters.agent')}
          />
        </Form.Item>
      ) : null}
    </>
  )
}

/**
 * URL holatida filtr qiymati DOIM massiv, backend esa YAKKA qiymat
 * kutadi ({@code SubscriberQuery}). Bu farqni bitta joyda tarjima
 * qilamiz, aks holda har chaqiruvda takrorlanardi.
 */
function asSingle(value: unknown): string | undefined {
  if (Array.isArray(value) && value.length > 0) return String(value[0])
  return undefined
}
